import {
    useEffect,
    useState,
    DependencyList,
    useRef,
    Dispatch,
    SetStateAction,
    useCallback,
    RefObject,
    useContext,
    RefCallback,
    EffectCallback,
    useMemo,
} from 'react';
import { getUniqueId } from './html';
import { ValidationRule, ValidationManager, ValidationContext, ValidationError } from '@/components/forms/validation';
import { Location } from 'history';
import { parseQueryString, buildUrl, getFilterQueryString } from './url';
import { debounce, throttle as throttleFunction } from 'lodash-es';
import { useSelector, useDispatch } from 'react-redux';
import { IState } from '@/state/IState';
import { ProfileType, ProjectTerms, ActiveUserData, CommentThreadType, UnitOfMeasurement } from '@/types';
import { DefaultCulture, CultureInfo, SupportedCultures } from './localization';
import { setCanScroll } from '@/state/global/actions';
import { useParams, useHistory, useLocation, useRouteMatch, generatePath } from 'react-router';
import { getProjectTermsAsBuyer } from '@/services/buyer-dashboard';
import { getProjectDeadlines, getProjectTerms } from '@/services/projects';
import { prefixTo } from '@/components/navigation/helpers';
import { DismissibleMessageKey } from '@/services/message-dismissal';
import { loadMessageDismissal, updateMessageDismissal } from '@/state/message-dismissals/creators';
import { ProjectUsers } from '@/services/projectUsers';

import { isEmpty } from 'lodash-es';
import { setCurrentCulture } from '@/state/global/creators';
import { useLingui } from '@lingui/react';
import { QueryKey, useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import type { Locale } from 'date-fns/locale';

// export const asyncUseEffect = (func) => {
//     useEffect(() => {
//         const a = async () => {
//             await func();
//         };
//         a();
//     });
// };

//Need to get rid of this redux nonsense, it's horrible
export function useAppDispatch() {
    return useDispatch<any>();
}

export function useAppSelector<TResult>(selector: (state: IState) => TResult) {
    return useSelector(selector);
}

export function useEffectDebounced(func: () => void, deps: DependencyList, delay: number) {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const callback = useCallback(debounce(func, delay), deps);

    useEffect(() => {
        callback();

        return function cleanup() {
            callback.cancel();
        };
    }, [callback]);
}

//Modified from https://dev.to/gabe_ragland/debouncing-with-react-hooks-jci
export function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

export function useForceUpdate() {
    const [, setCounter] = useState(1);

    return useCallback(() => {
        setCounter((counter) => counter + 1);
    }, []);
}

//Modified from https://overreacted.io/making-setinterval-declarative-with-react-hooks/
export function useInterval(callback: () => void, delay: number) {
    const savedCallback = useRef<() => void>();

    // Remember the latest callback.
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    // Set up the interval.
    useEffect(() => {
        function tick() {
            if (savedCallback.current) {
                savedCallback.current();
            }
        }

        if (delay !== null) {
            const intervalId = setInterval(tick, delay);
            return () => clearInterval(intervalId);
        }
    }, [delay]);
}

export function useUniqueId(): string {
    const uniqueId = useRef(getUniqueId());

    return uniqueId.current;
}

export function useTimestamp(deps?: DependencyList): number {
    const [cacheKey, setCacheKey] = useState<number>(new Date().valueOf());

    useEffect(() => {
        setCacheKey(new Date().valueOf());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return cacheKey;
}

export function useCacheBuster(url: string, deps?: DependencyList): string {
    const cacheKey = useTimestamp(deps);
    const urlParam = '__=' + encodeURIComponent(cacheKey);

    if (url.indexOf('?') > -1) {
        return url + '&' + urlParam;
    }

    return url + '?' + urlParam;
}

export function useValidationManager(rules: ValidationRule[] = []) {
    const [validationManager] = useState(new ValidationManager());

    useEffect(() => {
        rules.forEach((rule) => {
            validationManager.addRule(rule);
        });

        return function cleanup() {
            rules.forEach((rule) => {
                validationManager.removeRule(rule);
            });
        };
    }, [rules, validationManager]);

    return validationManager;
}

// export function useValidationManager2(func: (v: ValidationManager) => void) {
//     const validationManager = new ValidationManager();

//     useEffect(() => {
//         return func(validationManager);
//     }, [validationManager]);

//     return validationManager;
// }

export function useQueryString<T = any>(location: Location): T {
    const [queryParams, setQueryParams] = useState(parseQueryString(location.search));
    const firstRunRef = useRef(true);

    useEffect(() => {
        if (!firstRunRef.current) {
            setQueryParams(parseQueryString(location.search));
        }

        firstRunRef.current = false;
    }, [location]);

    return queryParams;
}

export function useMergeState<T = { [key: string]: any }>(
    initialState: T
): [T, Dispatch<Partial<T>>, Dispatch<SetStateAction<T>>] {
    const [value, setValue] = useState(initialState);

    const mergeValue = useCallback((updates: Partial<T>) => setValue((oldValue) => ({ ...oldValue, ...updates })), []);

    return [value, mergeValue, setValue];
}

class CancellationToken {
    private canceled = false;

    cancel() {
        this.canceled = true;
    }

    isCanceled() {
        return this.canceled;
    }
}

export function useAsyncStateWithIsLoading<T>(
    func: () => Promise<T>,
    deps: DependencyList,
    initialValue: T
): [T, Dispatch<SetStateAction<T>>, () => void, boolean] {
    const [data, setData] = useState(initialValue);
    const loadingCounter = useRef(0);
    const [isLoading, setIsLoading] = useState(false);

    const loadData = useCallback(async function loadData(cancelToken?: CancellationToken) {
        function shouldCancel() {
            if (cancelToken && cancelToken instanceof CancellationToken && cancelToken.isCanceled()) {
                return true;
            }

            return false;
        }

        loadingCounter.current = loadingCounter.current + 1;
        setIsLoading(loadingCounter.current > 0);
        const data = await func();
        if (!shouldCancel()) {
            setData(data);
        }
        loadingCounter.current = loadingCounter.current - 1;
        setIsLoading(loadingCounter.current > 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    useEffect(() => {
        const cancelToken = new CancellationToken();

        loadData(cancelToken);

        return () => {
            cancelToken.cancel();
        };
    }, [loadData]);

    return [data, setData, loadData, isLoading];
}

/**
 * Fetch data from an api and then set the data response to the state async
 * @param func - The function to fetch the data from.
 * @param deps - The dependencies to pass to the function.
 * @param initialValue - The initial value to set the data to.
 * @returns A tuple containing the data, a function to set the data, and a function to load the data.
 *
 * Example:
 * const [data, setData, loadData] = useAsyncState(
 *     () => getLandingPageContacts(projectId, activeConstructionPhaseId), // callback function
 *     [projectId, activeConstructionPhaseId], // dependencies
 *     [] // initial value
 * );
 */
export function useAsyncState<T>(
    func: () => Promise<T>,
    deps: DependencyList,
    initialValue: T
): [T, Dispatch<SetStateAction<T>>, () => Promise<void>] {
    const [data, setData] = useState(initialValue);

    const loadData = useCallback(async function loadData(cancelToken?: CancellationToken) {
        function shouldCancel() {
            if (cancelToken && cancelToken instanceof CancellationToken && cancelToken.isCanceled()) {
                return true;
            }

            return false;
        }

        const data = await func();
        if (!shouldCancel()) {
            setData(data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    useEffect(() => {
        const cancelToken = new CancellationToken();

        loadData(cancelToken);

        return () => {
            cancelToken.cancel();
        };
    }, [loadData]);

    return [data, setData, loadData];
}

export function useAsyncStateDebounced<T>(
    func: () => Promise<T>,
    deps: DependencyList,
    initialValue: T,
    delay: number
): [T, Dispatch<SetStateAction<T>>, () => void, boolean] {
    const [data, setData] = useState(initialValue);
    const [isLoading, setIsLoading] = useState(false);

    const loadData = useCallback(async function loadData() {
        setData(await func());
        setIsLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    useEffect(() => {
        setIsLoading(true);
    }, [loadData]);

    useEffectDebounced(
        () => {
            loadData();
        },
        [loadData],
        delay
    );

    return [data, setData, loadData, isLoading];
}

/**
 * Gets markdown from file
 * @param path
 */
export function useGetMarkdown(path: string) {
    const [md] = useAsyncState(
        () =>
            fetch(path).then((response) => {
                return response.text();
            }),
        [path],
        ''
    );
    return md;
}

export function useClickOutside<T extends Node>(
    ref: RefObject<T>,
    handler: (e: MouseEvent) => void,
    capture: boolean = false
) {
    useEffect(() => {
        function onClick(e: MouseEvent) {
            if (e.target && ref.current && e.target instanceof Node && ref.current.contains(e.target)) {
                return;
            }

            handler(e);
        }
        document.addEventListener('click', onClick, capture);

        return function cleanup() {
            document.removeEventListener('click', onClick, capture);
        };
    }, [ref, handler, capture]);
}

// export function useClickOutside2<T extends Node>(refs: RefObject<T>[], handler: () => void, capture: boolean = false) {
//     useEffect(() => {
//         function onClick(e: MouseEvent) {
//             if (refs.some((ref) => e.target && ref.current && e.target instanceof Node && ref.current.contains(e.target))) {
//                 return;
//             }
//             // if () {
//             //     return;
//             // }

//             handler();
//         }
//         document.addEventListener('click', onClick, capture);

//         return function cleanup() {
//             document.removeEventListener('click', onClick, capture);
//         };
//     }, [refs, handler, capture]);
// }

// export function useOnWindowScroll<T extends Node>(ref: RefObject<T>, handler: () => void, throttle: number | null = null) {
//     useEffect(() => {
//         let onScroll = (e: Event) => {
//             handler();
//         };

//         if (throttle) {
//             onScroll = throttleFunction(onScroll, throttle);
//         }

//         window.addEventListener('scroll', onScroll);

//         return function cleanup() {
//             window.removeEventListener('scroll', onScroll);
//         };
//     }, [ref.current, handler]);
// }

export function useOnWindowResize(handler: () => void, throttle: number | null = null) {
    useEffect(() => {
        let onResize = (e: UIEvent) => {
            handler();
        };

        if (throttle) {
            onResize = throttleFunction(onResize, throttle);
        }

        window.addEventListener('resize', onResize);

        return function cleanup() {
            window.removeEventListener('resize', onResize);
        };
    }, [handler, throttle]);
}

export function useIsMobile() {
    return useSelector((state: IState) => state.global.windowWidth < 769);
}

function useProfileType() {
    return useSelector((state: IState) => state.global.activeProfile && state.global.activeProfile.type);
}

export function useIsSeller() {
    const impersonationInfo = useImpersonationInfo();
    const profileType = useProfileType();

    return profileType === ProfileType.Seller && impersonationInfo === null;
}

export function useIsBuyer() {
    const impersonationInfo = useImpersonationInfo();
    const profileType = useProfileType();

    return profileType === ProfileType.Buyer || impersonationInfo !== null;
}

export function useIsImpersonating() {
    return useImpersonationInfo() !== null;
}

export function useImpersonationInfo(): { loading: boolean; unitName: string } | null {
    const buyerPermissions = useSelector((state: IState) => state.buyer?.permissions);
    const projectData = useSelector((state: IState) => state.buyer?.projectData);

    if (!buyerPermissions || !buyerPermissions.impersonating) {
        return null;
    }

    if (!projectData) {
        return {
            loading: true,
            unitName: '',
        };
    }

    return {
        loading: false,
        unitName: projectData.unitName,
    };
}

export function useCurrentCulture(): [CultureInfo | null, (cultureOrLocale: CultureInfo | string) => CultureInfo] {
    const currentCulture = useSelector((state: IState) => state.global.currentCulture);
    const dispatch = useAppDispatch();

    const setter = (cultureOrLocale: CultureInfo | string) => {
        let culture: CultureInfo | null | undefined = null;
        if (typeof cultureOrLocale === 'string') {
            culture = SupportedCultures.find((x) => x.locale === cultureOrLocale);
        } else {
            culture = cultureOrLocale;
        }
        if (!culture) {
            culture = DefaultCulture;
        }
        localStorage.setItem('lastLocale', culture.locale);
        setCurrentCulture(dispatch, culture);

        return culture;
    };

    return [currentCulture, setter];
}

export function useDocumentTypes() {
    return useSelector((state: IState) => {
        if (!state.global.activeProfile) {
            return null;
        }

        if (state.global.activeProfile.type === ProfileType.Seller && state.seller.documentTypes) {
            return state.seller.documentTypes;
        }

        if (state.global.activeProfile.type === ProfileType.Buyer && state.buyer.documentTypes) {
            return state.buyer.documentTypes;
        }

        return null;
    });
}

export function useDocumentType(documentTypeId: number | null) {
    return useSelector((state: IState) => {
        if (!documentTypeId) {
            return null;
        }

        if (!state.global.activeProfile) {
            return null;
        }

        if (state.global.activeProfile.type === ProfileType.Seller && state.seller.documentTypes) {
            return state.seller.documentTypes.find((x) => x.id === documentTypeId) || null;
        }

        if (state.global.activeProfile.type === ProfileType.Buyer && state.buyer.documentTypes) {
            return state.buyer.documentTypes.find((x) => x.id === documentTypeId) || null;
        }

        return null;
    });
}

export function useRequestLanguage(func: (localeName: string, localeData: Locale) => void) {
    const [langCode, setLangCode] = useState('en-GB');
    const currentCulture = useSelector((state: IState) => state.global.currentCulture);

    useEffect(() => {
        (async function () {
            const dateFnsImport = currentCulture ? currentCulture.dateFnsImport : DefaultCulture.dateFnsImport;
            const dateFnsName = currentCulture ? currentCulture.dateFnsName : DefaultCulture.dateFnsName;
            // if (langCode !== 'en') {
            const locale = await dateFnsImport();
            // const locale = await import(`date-fns/locale/${dateFnsName}/index.js`);
            func(dateFnsName, locale);
            // }
            setLangCode(dateFnsName);
        })();
    }, [currentCulture, func]);

    return langCode;
}

export function useFocusElement() {
    const ref = useCallback((node) => {
        if (node !== null) {
            node.focus();
        }
    }, []);

    return ref;
}
/**
 * Will try and only make it scroll on load, and on change of Id, and not on reload of items.
 * @param scrollToId Id of expand/select item - the on that should be scrolled to
 */
export function useCanScroll(scrollToId: number | string | undefined) {
    const [prevId, setPrevId] = useState(scrollToId);
    const rCanScroll = useSelector((state: IState) => state.global.canScroll);
    const dispatch = useAppDispatch();
    useEffect(() => {
        // When we change Id, we can scroll again.
        if (prevId !== scrollToId) {
            setPrevId(scrollToId);
            dispatch(setCanScroll(true));
        }

        return () => {
            dispatch(setCanScroll(true));
        };
    }, [scrollToId, prevId, dispatch]);
    // useEffect(() => {
    //     // on initial load, will set it to false. This should be after the first scroll
    //     if (canScroll) {
    //         setCanScroll(false);
    //     }
    // }, []);
    // useEffect(() => {
    //     // Set can scroll to false, again hopefully after we've scrolled.
    //     if (canScroll) {
    //         setCanScroll(false);
    //     }
    // }, [canScroll]);
    return rCanScroll;
}

export function useDefaultScrollToId(paramName = 'id') {
    const [defaultExpandedId, setDefaultExpandedId] = useState<undefined | number>(undefined);

    let { [paramName]: paramId } = useParams<any>();

    useEffect(() => {
        if (paramId && !isNaN(+paramId)) setDefaultExpandedId(+paramId);
        else setDefaultExpandedId(undefined);
    }, [paramId]);

    return defaultExpandedId;
}
export function useDefaultScrollToStringId(paramName = 'id') {
    const [defaultExpandedId, setDefaultExpandedId] = useState<undefined | string>(undefined);

    let { [paramName]: paramId } = useParams<any>();

    useEffect(() => {
        if (paramId) setDefaultExpandedId(paramId);
        else setDefaultExpandedId(undefined);
    }, [paramId]);

    return defaultExpandedId;
}

export function useGetTerms() {
    const isBuyer = useIsBuyer();
    const isSeller = useIsSeller();
    const permissions = useSelector((state: IState) => state.buyer.permissions);
    const activeProject = useSelector((state: IState) => state.global.activeProject);
    const [terms, setTerms] = useState<ProjectTerms | null>(null);

    useEffect(() => {
        const setTermsAsBuyer = async () => {
            setTerms(await getProjectTermsAsBuyer(permissions!.residentialUnitId));
        };
        const setTermsAsSeller = async () => {
            setTerms(await getProjectTerms(activeProject!.id));
        };

        if (isSeller && activeProject) {
            setTermsAsSeller();
        }

        if (isBuyer && permissions) {
            setTermsAsBuyer();
        }
    }, [isSeller, isBuyer, permissions, activeProject]);

    return terms;
}

export function useGetActiveProfileName() {
    const projectData = useSelector((state: IState) => state.buyer.projectData);
    const activeProject = useSelector((state: IState) => state.global.activeProject);

    return activeProject ? activeProject.sellerProfileName : projectData ? projectData.sellerProfileName : null;
}

export function useGetProjectId() {
    const projectData = useSelector((state: IState) => state.buyer.projectData);
    const activeProject = useSelector((state: IState) => state.global.activeProject);
    if (activeProject) return activeProject.id;
    if (projectData) return projectData.projectId;
}

export function useGetActiveProjectId() {
    const activeProject = useSelector((state: IState) => state.global.activeProject);
    if (activeProject) return activeProject.id;
}

export function usePageVisibility() {
    const [visible, setVisible] = useState(!document.hidden);

    useEffect(() => {
        function handleVisibilityChange() {
            setVisible(!document.hidden);
        }

        document.addEventListener('visibilitychange', handleVisibilityChange, false);

        return function cleanup() {
            document.removeEventListener('visibilitychange', handleVisibilityChange, false);
        };
    });

    return visible;
}

export function useWindowFocus() {
    const [visible, setVisible] = useState(!document.hidden && document.hasFocus());

    useEffect(() => {
        function handleFocusChange() {
            setVisible(document.hasFocus());
        }

        window.addEventListener('focus', handleFocusChange, false);
        window.addEventListener('blur', handleFocusChange, false);

        return function cleanup() {
            window.removeEventListener('focus', handleFocusChange, false);
            window.removeEventListener('blur', handleFocusChange, false);
        };
    });

    return visible;
}

export function useInputValidation<T = any>(
    value: T,
    func: (valueRef: RefObject<T>) => ValidationRule[],
    deps: DependencyList
) {
    const [invalid, setInvalid] = useState(false);
    const currentValueRef = useRef(value);
    const componentRef = useRef(Symbol('Validation'));

    const context = useContext(ValidationContext);
    const validationManager = context.validationManager;

    useEffect(() => {
        currentValueRef.current = value;
    }, [value]);

    useEffect(() => {
        if (!validationManager) {
            return;
        }

        function handleClear() {
            setInvalid(false);
        }

        function handleValidate(errors: ValidationError[]) {
            setInvalid(errors.filter((x) => x.component === componentRef.current).length > 0);
        }

        validationManager.onValidate.addHandler(handleValidate);
        validationManager.onClear.addHandler(handleClear);

        const rules = func(currentValueRef).map((x) => ({ ...x, component: componentRef.current }));

        validationManager.addRules(rules);

        return function () {
            validationManager.removeRules(rules);
            validationManager.onValidate.removeHandler(handleValidate);
            validationManager.onClear.removeHandler(handleClear);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [func, validationManager, ...deps]);

    return [invalid];
}

export function usePrevious<T>(value: T) {
    const ref = useRef<T>();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

export function useProjectRelativeRedirect() {
    const project = useSelector((state: IState) => state.global.activeProject!);
    const history = useHistory();

    return (to: string) => history.push(prefixTo(to, `/projects/${project.id}`));
}

// const IntersectionObserverSupported = typeof IntersectionObserver !== 'undefined';
// export function useIntersectionObserver(element: RefObject<HTMLElement>) {
//     const observer = useRef<IntersectionObserver>();
//     const [visible, setVisible] = useState(false);

//     console.log('element', element);

//     useEffect(() => {
//         const observedElement = element.current;
//         console.log('observedElement', observedElement);

//         if (IntersectionObserverSupported && observedElement) {
//             const handleObserve = (entries, observer) => {
//                 console.log('entries', entries);
//                 if (!observer) {
//                     return;
//                 }

//                 setVisible(entries[0].isIntersecting);
//             };

//             observer.current = new IntersectionObserver(handleObserve, {
//                 rootMargin: '0px',
//                 threshold: 0.1,
//             });

//             if (observer.current) {
//                 observer.current.observe(observedElement);
//             }
//         } else {
//             setVisible(true);
//         }

//         return function cleanup() {
//             if (observer.current) {
//                 if (observedElement) {
//                     observer.current.unobserve(observedElement);
//                 }
//                 observer.current = undefined;
//             }
//         };
//     }, []);

//     return visible;
// }
export const IntersectionObserverSupported = typeof IntersectionObserver !== 'undefined';
export function useIntersectionObserver<T extends HTMLElement>(): [RefCallback<T>, boolean, RefObject<T>] {
    const currentElement = useRef<T | null>(null);
    const [visible, setVisible] = useState(false);

    const observer = useRef<IntersectionObserver>();

    useEffect(() => {
        return function () {
            if (observer.current) {
                observer.current.disconnect();
            }
        };
    }, []);

    function createObserver(element: T) {
        if (observer.current && currentElement.current && element !== currentElement.current) {
            observer.current.unobserve(currentElement.current);
        }

        if (IntersectionObserverSupported) {
            if (!observer.current) {
                observer.current = new IntersectionObserver(
                    (entries, observer) => {
                        if (!observer) {
                            return;
                        }

                        setVisible(entries[0].isIntersecting);
                    },
                    {
                        rootMargin: '0px',
                        threshold: 0.1,
                    }
                );
            }

            if (element) {
                observer.current.observe(element);
            }
        } else {
            setVisible(true);
        }

        currentElement.current = element;
    }

    return [createObserver, visible, currentElement];
}

export function useMessageDismissal(key: DismissibleMessageKey, projectId: number | null): [boolean | null, () => void] {
    const dispatch = useAppDispatch();

    const initialLoad = useRef(true);
    useEffect(() => {
        if (initialLoad) {
            dispatch(loadMessageDismissal(key, projectId));
        }
        initialLoad.current = false;
    }, [key, projectId, dispatch]);

    const items = useSelector((state: IState) => state.messageDismissals.items);
    const item = items.find((x) => x.key === key && x.projectId === projectId);

    // const [value, setValue] = useAsyncState(() => getMessageDismissalValue(key, projectId), [key, projectId], null);

    const dismiss = useCallback(() => {
        dispatch(updateMessageDismissal(key, true, projectId));
    }, [key, projectId, dispatch]);

    return [item?.value ?? null, dismiss];
}

export function useActiveProjectBuyer(): ActiveUserData[] {
    const [activeUsers, setActiveUsers] = useState<ActiveUserData[]>([]);

    useEffect(() => {
        var userApi = new ProjectUsers();
        const receiveActiveBuyersHandler = (users: ActiveUserData[]) => {
            setActiveUsers(users);
        };

        (async () => {
            setActiveUsers(await userApi.getActiveBuyers());
        })();

        userApi.addReceiveActiveBuyersHandler(receiveActiveBuyersHandler);
        return () => {
            userApi.removeReceiveActiveBuyersHandler(receiveActiveBuyersHandler);
        };
    }, []);

    return activeUsers;
}

const fixQueryStringArrays = (parseData, defaultState) => {
    let returnData = { ...defaultState };
    if (!isEmpty(parseData)) {
        for (const propertyName in defaultState) {
            if (typeof defaultState[propertyName] === 'boolean') {
                returnData[propertyName] = parseData[propertyName] === 'true';
            } else if (
                parseData[propertyName] &&
                Array.isArray(returnData[propertyName]) &&
                !Array.isArray(parseData[propertyName])
            ) {
                returnData[propertyName] = [parseData[propertyName]];
            } else if (propertyName in parseData) {
                returnData[propertyName] = parseData[propertyName];
            }
        }
    }

    return returnData;
};

/**
 *  NB: Only use number[] | string[] | string | number
 *
 *  Enumerations also works.
 *  Basically only stuff that will work fine in the URL
 */
export function useQueryState<T>(initialState: T): [T, (v: T) => void] {
    const frozenInitialState = useRef(initialState);

    const parsedData = parseQueryString(window.location.search);
    const fixedData = fixQueryStringArrays(parsedData, initialState);
    const history = useHistory();
    const location = useLocation();

    const [data, setData] = useState<T>(fixedData);

    const previousLocation = usePrevious(location.search);
    useEffectSkipFirst(() => {
        if (previousLocation !== location.search) {
            const newData = parseQueryString(location.search);
            setData(fixQueryStringArrays(newData, frozenInitialState.current));
        }
    }, [location]);

    const onSetData = useCallback(
        (newValue: T) => {
            // setData(newValue);
            const qs = getFilterQueryString(newValue as Object);

            if (qs !== location.search) {
                history.push(
                    buildUrl({
                        path: location.pathname,
                        search: qs,
                        hash: location.hash,
                    })
                );
            }
        },
        [history, location]
    );

    return [data, onSetData];
}

export function useEffectSkipFirst(effect: EffectCallback, deps: DependencyList) {
    const skipped = useRef(false);

    useEffect(() => {
        if (skipped.current) {
            return effect();
        }

        skipped.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, skipped]);
}

export function useAsRef<T>(value: T) {
    const ref = useRef(value);
    ref.current = value;
    return ref;
}

export function useParamReplace<T extends { [paramName: string]: string | number | boolean | undefined }>(path?: string) {
    const params = useParams();
    const match = useRouteMatch();
    const location = useLocation();

    const paramsRef = useAsRef(params);
    const locationRef = useAsRef(location);
    const matchRef = useAsRef(match);

    return useCallback(
        (replacementParams: T) => {
            return buildUrl({
                path: generatePath(path ?? matchRef.current.path, { ...paramsRef.current, ...replacementParams }),
                search: locationRef.current.search,
                hash: locationRef.current.hash,
            });
        },
        [paramsRef, matchRef, locationRef, path]
    );
}

// export function useParamReplace<T extends { [paramName: string]: string | number | boolean | undefined }>(path?: string) {
//     const params = useParams();
//     const match = useRouteMatch();
//     const location = useLocation();

//     return (replacementParams: T) => {
//         return buildUrl({
//             path: generatePath(path ?? match.path, { ...params, ...replacementParams }),
//             search: location.search,
//             hash: location.hash,
//         });
//     };
// }

type UnreadMessageCount = { [key in keyof typeof CommentThreadType]: number } & {
    readonly total: number;
};

export function useThreadUnreadMessageCount(threadId: number): number | undefined {
    const profileType = useProfileType();
    const buyerThreads = useSelector((state: IState) => state.buyer.commentThreads);
    const sellerThreads = useSelector((state: IState) => state.seller.projectOverview.comments);

    return useMemo(() => {
        if (profileType === ProfileType.Buyer && buyerThreads) {
            return buyerThreads.find((x) => x.threadId === threadId)?.unreadMessages;
        } else if (profileType === ProfileType.Seller && sellerThreads) {
            return sellerThreads.find((x) => x.threadId === threadId)?.unreadMessages;
        }
    }, [threadId, profileType, buyerThreads, sellerThreads]);
}

export function useUnreadThreadCount(): UnreadMessageCount {
    const activeProfile = useAppSelector((state) => state.global.activeProfile);
    const buyerCommentThreads = useAppSelector((state) => state.buyer.commentThreads);
    const sellerProjectOverview = useAppSelector((state) => state.seller.projectOverview);

    const commentThreads = useMemo(() => {
        if (activeProfile?.type === ProfileType.Buyer) {
            return buyerCommentThreads.map((x) => ({ unreadMessages: x.unreadMessages, type: x.type }));
        } else if (activeProfile?.type === ProfileType.Seller) {
            return sellerProjectOverview.comments?.map((x) => ({
                unreadMessages: x.unreadMessages,
                type: x.type,
            }));
        }

        return null;
    }, [activeProfile, buyerCommentThreads, sellerProjectOverview]);

    return useMemo(() => {
        const unreadThreads = commentThreads?.filter((x) => x.unreadMessages) ?? [];
        return Object.values(CommentThreadType).reduce(
            (acc, current) => {
                acc[current] = unreadThreads.filter((x) => x.type === current).length;
                return acc;
            },
            { total: unreadThreads.length }
        ) as UnreadMessageCount;
    }, [commentThreads]);
}
export function useIsWhiteLabel() {
    const isBuyer = useIsBuyer();
    return useSelector((state: IState) => state.buyer.projectData?.useWhiteLabel ?? false) && isBuyer;
}

export function useGetActiveProfile() {
    const activeProfile = useSelector((state: IState) => state.global.activeProfile);
    return activeProfile;
}
export function useGetActiveProject() {
    const activeProject = useSelector((state: IState) => state.global.activeProject);
    if (activeProject) return activeProject;
}

export function useGetDefaultProjectUnitOfMeasurement() {
    const projectData = useSelector((state: IState) => state.buyer.projectData);
    const activeProject = useSelector((state: IState) => state.global.activeProject);
    if (projectData) return projectData.defaultUnitOfMeasurement;
    if (activeProject) return activeProject.defaultUnitOfMeasurement;
    return UnitOfMeasurement.Millimeter;
}
export function useShouldDisplayDefaultTextOnOptions() {
    const isBuyer = useIsBuyer();

    const activeProject = useSelector((state: IState) => state.global.activeProject);
    const projectData = useSelector((state: IState) => state.buyer.projectData);
    if (!isBuyer) return activeProject?.showDefaultTextOnOptions;
    if (projectData) return projectData.showDefaultTextOnOptions;
}

// Named to avoid colliding with the useRooms query hook in services/rooms.ts.
// Profile-aware like useShouldDisplayDefaultTextOnOptions: sellers read the active project,
// buyers read their project data.
export function useProjectRoomsEnabled() {
    const isBuyer = useIsBuyer();
    const activeProjectUseRooms = useSelector((state: IState) => state.global.activeProject?.useRooms ?? false);
    const projectDataUseRooms = useSelector((state: IState) => state.buyer.projectData?.useRooms ?? false);
    return isBuyer ? projectDataUseRooms : activeProjectUseRooms;
}

// Mirrors the backend's budget grouping condition (BuyerDashboardService): the budget is
// grouped by room only when the project is on the new options view and has rooms enabled;
// otherwise it is grouped by discipline/category. Buyer-side only, like the budget itself.
export function useBudgetGroupedByRoom() {
    const roomsEnabled = useProjectRoomsEnabled();
    const useLegacyOptionsView = useSelector((state: IState) => state.buyer.projectData?.useLegacyOptionsView ?? false);
    return !useLegacyOptionsView && roomsEnabled;
}

export function useCategories() {
    const itemCategories = useSelector((state: IState) => state.global.itemCategories);

    return itemCategories;
}

export const useGetCorrectLanguageCodeForPhoneInput = (): 'se' | 'no' => {
    const [cultureInfo] = useCurrentCulture();
    const currentLanguage = cultureInfo?.languageCode;
    return currentLanguage === 'sv' ? 'se' : 'no';
};

const defaultTitle = window.document.title;
export function usePageTitle(title: string | null | undefined) {
    const { i18n } = useLingui();
    // const project = useSelector((state: IState) => state.global.activeProject);
    useEffect(() => {
        // const parts = [project?.name, title]
        window.document.title = title ?? defaultTitle;

        return () => {
            window.document.title = defaultTitle;
        };
    }, [title, i18n.locale]);
}

export function useGetRubustechIoUrl(path: string) {
    const getLanguagePathFromCulture = (culture: CultureInfo | null) => {
        switch (culture?.languageCode) {
            case 'en':
                return '';
            case 'sv':
                return '/sv';
            case 'nb':
                return '/no';
            case 'de':
                return '/de';
            default:
                return '';
        }
    };

    const [currentCulture] = useCurrentCulture();
    return `https://www.rucoria.com${getLanguagePathFromCulture(currentCulture)}/${path}`;
}

type GetterFunction<TReturn, TParams extends any[]> = (...params: TParams) => Promise<TReturn>;
export function createQueryHook<TReturn, TParams extends any[]>(
    func: GetterFunction<TReturn, TParams>,
    key: string = func.name
) {
    if (!key) {
        throw new Error('Key is required for anonymous functions');
    }

    const getter = function (options: Omit<UseQueryOptions<TReturn>, 'queryKey' | 'queryFn'>, ...params: TParams) {
        return useQuery({
            ...options,
            queryKey: [key, ...params],
            queryFn: () => func(...params),
        });
    };

    const ret = function (...params: TParams) {
        return getter({}, ...params);
    };

    ret.withOptions = getter;

    return ret;
}

/**
 * Creates a React Query mutation hook from an async function and automatically infers the mutation variables
 * from the function's parameter list using TypeScript's Parameters utility type.
 *
 * Contract
 * - Input: an async function (e.g., updateFn) with any number of parameters, and an optional invalidation key.
 * - Variables: a tuple matching the original function's parameters (Parameters<TFunc>).
 * - Output: a useMutation result where mutate/mutateAsync accept the parameter tuple and invoke the original function.
 * - Side effect: on successful mutation, invalidates the provided query key (if specified).
 *
 * Example
 * const update = createInvalidatingMutationHookFrom(updateOptionPresetGroup, ['optionPresetGroups', projectId]);
 * await update.mutateAsync([presetGroupId, options]);
 * // or
 * update.mutate([presetGroupId, options]);
 *
 * Notes
 * - This avoids duplicating parameter types for mutations that mirror an existing API function.
 */
export function createInvalidatingMutationHookFrom<TFunc extends (...args: any[]) => Promise<any>>(
    func: TFunc,
    key?: QueryKey
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (variables: Parameters<TFunc>) => func(...(variables as unknown as Parameters<TFunc>)),
        onSuccess: () => {
            if (key) {
                queryClient.invalidateQueries({ queryKey: [key] });
            }
        },
    });
}

/**
 * Creates a mutation hook that binds a fixed tuple of arguments to the provided async function.
 * The resulting mutation requires no variables when calling mutate/mutateAsync.
 *
 * Example
 * const m = createInvalidatingMutationHookFromWithFixedVars(updateFn, [id, payload], ['key']);
 * await m.mutateAsync(); // calls updateFn(id, payload)
 */
export function createInvalidatingMutationHookFromWithFixedVars<TFunc extends (...args: any[]) => Promise<any>>(
    func: TFunc,
    fixedArgs: Parameters<TFunc>,
    key?: QueryKey
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => func(...(fixedArgs as unknown as Parameters<TFunc>)),
        onSuccess: () => {
            if (key) {
                queryClient.invalidateQueries({ queryKey: [key] });
            }
        },
    });
}

function waitForHubspot() {
    return new Promise((resolve) => {
        const interval = setInterval(() => {
            try {
                const { loaded } = window.HubSpotConversations.widget.status();
                if (loaded) {
                    clearInterval(interval);
                    resolve(true);
                }
            } catch (e) {
                //nothing
            }
        }, 100);
    });
}

export function useHubspotLoaded() {
    const [loaded, setLoaded] = useState(false);
    useEffect(() => {
        waitForHubspot().then((x) => {
            setLoaded(true);
        });
    }, []);

    return loaded;
}
export function useGetProjectDeadlines() {
    const projectId = useGetProjectId();
    const { data: deadlines } = useQuery({
        queryKey: ['projectDeadlines', projectId],
        queryFn: () => getProjectDeadlines(projectId!),
        enabled: !!projectId,
    });
    return deadlines;
}
